import React, { useState } from "react";
import {
  Calendar,
  Clock,
  Users,
  BookOpen,
  Globe,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Bell,
  GraduationCap,
  CheckCircle2,
  BookMarked,
  Target,
  Lightbulb,
  ClipboardList
} from "lucide-react";
import DemoSidebar from './DemoSidebar';
import { SCHEDULE_DATA } from './scheduleData';

const SchedulePage = () => {
  const [currentWeek, setCurrentWeek] = useState(SCHEDULE_DATA);
  const [selectedClass, setSelectedClass] = useState(null);

//   const handlePreviousWeek = () => {
//     // In a real app, this would fetch the previous week's data
//     console.log("Navigate to previous week");
//   };

//   const handleNextWeek = () => {
//     // In a real app, this would fetch the next week's data
//     console.log("Navigate to next week");
//   };

  const renderClassCard = (classInfo) => {
    return React.createElement('div', {
      className: 'bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer',
      onClick: () => setSelectedClass(classInfo),
      key: classInfo.id
    }, [
      // Time and subject header
      React.createElement('div', {
        className: 'flex justify-between items-start mb-3',
        key: 'header'
      }, [
        React.createElement('div', {
          className: 'flex items-center space-x-3'
        }, [
          React.createElement(Clock, {
            className: 'w-4 h-4 text-gray-500'
          }),
          React.createElement('span', {
            className: 'text-sm font-medium text-gray-900'
          }, classInfo.time)
        ]),
        React.createElement('div', {
          className: 'flex items-center space-x-2'
        }, [
          classInfo.homeworkDue && React.createElement('span', {
            className: 'bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full'
          }, 'Homework Due'),
          React.createElement('button', {
            className: 'text-gray-400 hover:text-gray-500',
            onClick: (e) => {
              e.stopPropagation();
              console.log('More options');
            }
          }, React.createElement(MoreVertical, {
            className: 'w-4 h-4'
          }))
        ])
      ]),

      // Subject and topic
      React.createElement('div', {
        className: 'mb-3',
        key: 'subject'
      }, [
        React.createElement('h3', {
          className: 'text-lg font-semibold text-gray-900 mb-1'
        }, classInfo.subject),
        React.createElement('p', {
          className: 'text-sm text-gray-500'
        }, `Topic: ${classInfo.topic}`)
      ]),

      // Class details
      React.createElement('div', {
        className: 'grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3 text-sm text-gray-600',
        key: 'details'
      }, [
        React.createElement('div', {
          className: 'flex items-center space-x-2'
        }, [
          React.createElement(Users, {
            className: 'w-4 h-4'
          }),
          React.createElement('span', null, `${classInfo.students} students`)
        ]),
        React.createElement('div', {
          className: 'flex items-center space-x-2'
        }, [
          React.createElement(GraduationCap, {
            className: 'w-4 h-4'
          }),
          React.createElement('span', null, classInfo.class)
        ]),
        React.createElement('div', {
          className: 'flex items-center space-x-2'
        }, [
          React.createElement(BookMarked, {
            className: 'w-4 h-4'
          }),
          React.createElement('span', null, classInfo.room)
        ])
      ]),

      // Materials
      React.createElement('div', {
        className: 'text-sm text-gray-600',
        key: 'materials'
      }, [
        React.createElement('div', {
          className: 'flex items-center space-x-2 mb-2'
        }, [
          React.createElement(BookOpen, {
            className: 'w-4 h-4 text-gray-500'
          }),
          React.createElement('span', {
            className: 'font-medium'
          }, 'Materials:')
        ]),
        React.createElement('div', {
          className: 'flex flex-wrap gap-2 ml-6'
        }, classInfo.materials.map((material, index) =>
          React.createElement('span', {
            key: index,
            className: 'bg-gray-100 px-2 py-1 rounded-md text-xs'
          }, material)
        ))
      ]),

      // Objectives preview
      React.createElement('div', {
        className: 'mt-3 pt-3 border-t border-gray-100',
        key: 'objectives'
      }, [
        React.createElement('div', {
          className: 'flex items-center space-x-2 mb-2 text-sm text-gray-600'
        }, [
          React.createElement(Target, {
            className: 'w-4 h-4 text-gray-500'
          }),
          React.createElement('span', {
            className: 'font-medium'
          }, 'Key Objectives')
        ]),
        React.createElement('ul', {
          className: 'ml-6 text-xs text-gray-500 space-y-1'
        }, classInfo.objectives.slice(0, 2).map((objective, index) =>
          React.createElement('li', {
            key: index,
            className: 'flex items-center space-x-2'
          }, [
            React.createElement('span', {
              className: 'w-1 h-1 bg-gray-400 rounded-full'
            }),
            objective
          ])
        ))
      ])
    ]);
  };

  const renderDayColumn = (day) => {
    return React.createElement('div', {
      className: 'flex-1 min-w-[300px] max-w-[400px]',
      key: day.date
    }, [
      // Day header
      React.createElement('div', {
        className: 'bg-gray-50 px-4 py-3 rounded-t-lg border border-gray-200',
        key: 'day-header'
      }, [
        React.createElement('h3', {
          className: 'font-medium text-gray-900'
        }, day.date)
      ]),
      // Classes container
      React.createElement('div', {
        className: 'p-3 space-y-4 border border-t-0 border-gray-200 rounded-b-lg bg-white',
        key: 'classes'
      }, day.classes.map(classInfo => renderClassCard(classInfo)))
    ]);
  };

  const ClassDetailModal = ({ classInfo, onClose }) => {
    if (!classInfo) return null;

    return React.createElement('div', {
      className: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4',
      onClick: onClose
    },
      React.createElement('div', {
        className: 'bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto',
        onClick: e => e.stopPropagation()
      }, [
        React.createElement('div', {
          className: 'p-6 space-y-6'
        }, [
          // Header
          React.createElement('div', {
            className: 'flex justify-between items-start'
          }, [
            React.createElement('div', null, [
              React.createElement('h2', {
                className: 'text-2xl font-semibold text-gray-900'
              }, classInfo.subject),
              React.createElement('p', {
                className: 'text-gray-500'
              }, classInfo.class)
            ]),
            React.createElement('button', {
              className: 'text-gray-400 hover:text-gray-500',
              onClick: onClose
            }, 'Close')
          ]),

          // Time and Location
          React.createElement('div', {
            className: 'flex items-center justify-between text-sm text-gray-600'
          }, [
            React.createElement('div', {
              className: 'flex items-center space-x-2'
            }, [
              React.createElement(Clock, { className: 'w-4 h-4' }),
              React.createElement('span', null, classInfo.time)
            ]),
            React.createElement('div', {
              className: 'flex items-center space-x-2'
            }, [
              React.createElement(BookMarked, { className: 'w-4 h-4' }),
              React.createElement('span', null, classInfo.room)
            ])
          ]),

          // Objectives
          React.createElement('div', {
            className: 'space-y-3'
          }, [
            React.createElement('h3', {
              className: 'text-lg font-medium text-gray-900 flex items-center space-x-2'
            }, [
              React.createElement(Target, { className: 'w-5 h-5 text-gray-600' }),
              React.createElement('span', null, 'Lesson Objectives')
            ]),
            React.createElement('ul', {
              className: 'space-y-2'
            }, classInfo.objectives.map((objective, index) =>
              React.createElement('li', {
                key: index,
                className: 'flex items-start space-x-2 text-gray-600'
              }, [
                React.createElement(CheckCircle2, { 
                  className: 'w-4 h-4 mt-1 text-green-500' 
                }),
                React.createElement('span', null, objective)
              ])
            ))
          ]),

          // Homework Details
          classInfo.homeworkDetails && React.createElement('div', {
            className: 'space-y-3'
          }, [
            React.createElement('h3', {
              className: 'text-lg font-medium text-gray-900 flex items-center space-x-2'
            }, [
              React.createElement(ClipboardList, { className: 'w-5 h-5 text-gray-600' }),
              React.createElement('span', null, 'Homework Details')
            ]),
            React.createElement('div', {
              className: 'bg-gray-50 rounded-lg p-4 space-y-2'
            }, [
              React.createElement('p', {
                className: 'text-sm text-gray-600'
              }, [
                React.createElement('span', {
                  className: 'font-medium'
                }, 'Type: '),
                classInfo.homeworkDetails.type
              ]),
              React.createElement('p', {
                className: 'text-sm text-gray-600'
              }, [
                React.createElement('span', {
                  className: 'font-medium'
                }, 'Assignment: '),
                classInfo.homeworkDetails.problems
              ]),
              React.createElement('p', {
                className: 'text-sm text-gray-600'
              }, [
                React.createElement('span', {
                  className: 'font-medium'
                }, 'Due: '),
                classInfo.homeworkDetails.dueNext
              ])
            ])
          ])
        ])
      ])
    );
  };

  return React.createElement('div', {
    className: 'flex h-screen overflow-hidden bg-gray-50'
  }, [
    React.createElement(DemoSidebar, { 
      key: 'sidebar',
      className: 'flex-shrink-0'
    }),
    
    React.createElement('div', {
      className: 'flex-1 flex flex-col min-w-0',
      key: 'main-content'
    }, [
      // Header
      React.createElement('div', {
        className: 'w-full border-b bg-white flex-shrink-0',
        key: 'header'
      },
        React.createElement('div', {
          className: 'flex items-center justify-between px-6 py-3'
        }, [
          React.createElement('h1', {
            className: 'text-lg font-medium text-gray-900'
          }, 'Weekly Schedule'),
          React.createElement('div', {
            className: 'flex items-center space-x-4'
          }, [
            React.createElement('div', {
              className: 'flex items-center space-x-2'
            }, [
              React.createElement(Globe, {
                className: 'w-4 h-4 text-gray-600'
              }),
              React.createElement('span', {
                className: 'text-sm text-gray-700'
              }, 'English')
            ]),
            React.createElement('div', {
              className: 'flex items-center space-x-2'
            }, [
              React.createElement('div', {
                className: 'w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center'
              },
                React.createElement(Users, {
                  className: 'w-4 h-4 text-gray-600'
                })
              ),
              React.createElement('span', {
                className: 'text-sm text-gray-700'
              }, 'Admin')
            ])
          ])
        ])
      ),

      // Week Navigation
      React.createElement('div', {
        className: 'bg-white border-b px-6 py-3 flex-shrink-0',
        key: 'week-nav'
      },
        React.createElement('div', {
          className: 'flex items-center justify-between'
        }, [
          React.createElement('div', {
            className: 'flex items-center space-x-4'
          }, [
            // React.createElement('button', {
            //   className: 'p-1 hover:bg-gray-100 rounded-full',
            //   onClick: handlePreviousWeek
            // }, React.createElement(ChevronLeft, {
            //   className: 'w-5 h-5 text-gray-600'
            // })),
            React.createElement('span', {
              className: 'font-medium text-gray-900'
            }, currentWeek.currentWeek),
            // React.createElement('button', {
            //   className: 'p-1 hover:bg-gray-100 rounded-full',
            //   onClick: handleNextWeek
            // }, React.createElement(ChevronRight, {
            //   className: 'w-5 h-5 text-gray-600'
            // }))
          ])
        ])
      ),

      // Schedule Content
      React.createElement('div', {
        className: 'flex-1 overflow-auto',
        key: 'schedule-content'
      },
        React.createElement('div', {
          className: 'p-6'
        },
          React.createElement('div', {
            className: 'flex space-x-4 overflow-x-auto pb-4'
          }, currentWeek.days.map(day => renderDayColumn(day)))
        )
      ),

      // Class Detail Modal
      selectedClass && React.createElement(ClassDetailModal, {
        classInfo: selectedClass,
        onClose: () => setSelectedClass(null)
    }),

    // Weekly Stats Summary
    // React.createElement('div', {
    //   className: 'bg-white border-t px-6 py-4 flex-shrink-0',
    //   key: 'stats'
    // },
    //   React.createElement('div', {
    //     className: 'grid grid-cols-2 md:grid-cols-4 gap-4'
    //   }, [
    //     // Total Classes
    //     React.createElement('div', {
    //       className: 'bg-gray-50 rounded-lg p-4',
    //       key: 'total-classes'
    //     }, [
    //       React.createElement('div', {
    //         className: 'text-sm text-gray-500 mb-1'
    //       }, 'Total Classes'),
    //       React.createElement('div', {
    //         className: 'text-2xl font-bold text-gray-900'
    //       }, currentWeek.stats.totalClasses)
    //     ]),
    //     // Total Students
    //     React.createElement('div', {
    //       className: 'bg-gray-50 rounded-lg p-4',
    //       key: 'total-students'
    //     }, [
    //       React.createElement('div', {
    //         className: 'text-sm text-gray-500 mb-1'
    //       }, 'Total Students'),
    //       React.createElement('div', {
    //         className: 'text-2xl font-bold text-gray-900'
    //       }, currentWeek.stats.totalStudents)
    //     ]),
    //     // Homework Due
    //     React.createElement('div', {
    //       className: 'bg-orange-50 rounded-lg p-4',
    //       key: 'homework-due'
    //     }, [
    //       React.createElement('div', {
    //         className: 'text-sm text-orange-600 mb-1'
    //       }, 'Homework Due'),
    //       React.createElement('div', {
    //         className: 'text-2xl font-bold text-orange-700'
    //       }, currentWeek.stats.homeworkDueCount)
    //     ]),
    //     // Upcoming Assessments
    //     React.createElement('div', {
    //       className: 'bg-blue-50 rounded-lg p-4',
    //       key: 'assessments'
    //     }, [
    //       React.createElement('div', {
    //         className: 'text-sm text-blue-600 mb-1'
    //       }, 'Upcoming Assessments'),
    //       React.createElement('div', {
    //         className: 'text-2xl font-bold text-blue-700'
    //       }, currentWeek.stats.upcomingAssessments)
    //     ])
    //   ])
    // )
  ])
]);
};

// Utility function to format time (if needed)
const formatTime = (timeString) => {
return timeString;
};

// Utility function to get status color
const getStatusColor = (status) => {
switch (status) {
  case 'completed':
    return 'text-green-600 bg-green-100';
  case 'pending':
    return 'text-yellow-600 bg-yellow-100';
  case 'urgent':
    return 'text-red-600 bg-red-100';
  default:
    return 'text-gray-600 bg-gray-100';
}
};

export default SchedulePage;